import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, Check, Smartphone, Monitor } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const Install = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Detectar se já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Detectar iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(ios);

    // Capturar o evento beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast({
        title: "Instalação não disponível",
        description: "Use o menu do seu navegador para instalar o app",
      });
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      toast({
        title: "✅ App instalado com sucesso!",
        description: "VYRA agora está no seu dispositivo",
      });
      setIsInstalled(true);
    }

    setDeferredPrompt(null);
  };

  if (isInstalled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-background to-background/80">
        <Card className="max-w-md w-full p-8 text-center space-y-6">
          <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
            <Check className="w-10 h-10 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold mb-2">App Instalado! 🎉</h1>
            <p className="text-muted-foreground">
              VYRA já está instalado no seu dispositivo
            </p>
          </div>
          <Button onClick={() => navigate("/today")} className="w-full">
            Abrir VYRA
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-background to-background/80">
      <Card className="max-w-md w-full p-8 space-y-6">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary to-primary/60 rounded-3xl flex items-center justify-center">
            <Download className="w-10 h-10 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold mb-2">Instalar VYRA</h1>
            <p className="text-muted-foreground">
              Adicione o VYRA à sua tela inicial para acesso rápido e funcionalidade offline
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Benefícios */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5">
              <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Acesso instantâneo</p>
                <p className="text-sm text-muted-foreground">
                  Abra direto da tela inicial
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5">
              <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Funciona offline</p>
                <p className="text-sm text-muted-foreground">
                  Use mesmo sem internet
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5">
              <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Notificações gentis</p>
                <p className="text-sm text-muted-foreground">
                  Lembretes adaptativos
                </p>
              </div>
            </div>
          </div>

          {/* Botão de instalação */}
          {!isIOS && deferredPrompt && (
            <Button onClick={handleInstallClick} className="w-full" size="lg">
              <Download className="w-5 h-5 mr-2" />
              Instalar Agora
            </Button>
          )}

          {/* Instruções iOS */}
          {isIOS && (
            <div className="space-y-4 p-4 border border-border rounded-lg">
              <div className="flex items-center gap-2 text-primary">
                <Smartphone className="w-5 h-5" />
                <p className="font-medium">Instruções para iOS</p>
              </div>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Toque no botão de compartilhar (quadrado com seta)</li>
                <li>Role para baixo e toque em "Adicionar à Tela Inicial"</li>
                <li>Toque em "Adicionar" no canto superior direito</li>
              </ol>
            </div>
          )}

          {/* Instruções Android/Desktop */}
          {!isIOS && !deferredPrompt && (
            <div className="space-y-4 p-4 border border-border rounded-lg">
              <div className="flex items-center gap-2 text-primary">
                <Monitor className="w-5 h-5" />
                <p className="font-medium">Como instalar</p>
              </div>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Abra o menu do navegador (⋮)</li>
                <li>Selecione "Instalar app" ou "Adicionar à tela inicial"</li>
                <li>Confirme a instalação</li>
              </ol>
            </div>
          )}
        </div>

        <Button variant="outline" onClick={() => navigate("/")} className="w-full">
          Voltar ao Início
        </Button>
      </Card>
    </div>
  );
};

export default Install;
